import { Link } from "react-router-dom";
import { Book, Code2, Server, Zap, Shield, Terminal, ArrowRight } from "lucide-react";

const CLOUD_LOGO = "https://customer-assets.emergentagent.com/job_eamico-core/artifacts/jlqt1nkd_Coding%20and%20design%20harmony%20%282%29.png";

const categories = [
  { icon: Book, title: "Getting Started", desc: "Set up your account and deploy your first project in under 5 minutes.", link: "/docs/getting-started/quick-start", links: ["Quick start guide", "Account setup", "First project", "First deployment"], color: "#ff9a4d" },
  { icon: Server, title: "Eamico Cloud", desc: "Deploy, configure, and manage your applications on the Aethernet.", link: "/docs/cloud/overview", links: ["Overview", "The Aethernet", "Edge Network", "Custom Domains"], color: "#34a853" },
  { icon: Terminal, title: "CLI Reference", desc: "Manage everything from your terminal with the Eamico CLI.", link: "/docs/cli/installation", links: ["Installation", "Commands", "Configuration"], color: "#ea4335" },
  { icon: Shield, title: "Security", desc: "Understand how we protect your code, data, and infrastructure.", link: "/docs/security/authentication", links: ["Authentication & 2FA", "Encryption", "Compliance", "Audit Logs"], color: "#ffc366" },
  { icon: Zap, title: "API Reference", desc: "Integrate Eamico into your workflow with our REST APIs.", link: "/docs/api/authentication", links: ["Authentication", "Projects", "Deployments", "Webhooks"], color: "#4285f4" },
];

const DocsPage = () => {
  return (
    <div data-testid="docs-page">
      {/* Hero */}
      <div className="bg-[#f8f9fa] border-b border-[#e8eaed]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16 sm:py-20">
          <h1 className="text-[32px] sm:text-[40px] lg:text-[48px] font-normal text-[#202124]" data-testid="docs-title">Documentation</h1>
          <p className="mt-4 text-base sm:text-lg text-[#5f6368] max-w-xl">
            Everything you need to build, deploy, and scale with Eamico.
          </p>
          <div className="mt-8 max-w-lg">
            <div className="relative">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9aa0a6]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <input type="text" placeholder="Search documentation..." className="w-full pl-11 pr-4 py-3 text-[14px] bg-white rounded-lg border border-[#dadce0] focus:border-[#ff9a4d] focus:ring-1 focus:ring-[#ff9a4d] outline-none transition-colors shadow-sm" data-testid="docs-search" />
            </div>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="docs-categories">
          {categories.map((cat, i) => {
            const Icon = cat.icon;
            return (
              <div key={i} className="p-8 rounded-xl border border-[#e8eaed] hover:border-[#dadce0] hover:shadow-sm transition-all" data-testid={`docs-category-${i}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: `${cat.color}15` }}>
                  {cat.iconImg ? (
                    <img src={cat.iconImg} alt="" className="w-6 h-6 rounded" />
                  ) : (
                    <Icon className="w-5 h-5" style={{ color: cat.color }} />
                  )}
                </div>
                <h3 className="text-[18px] font-medium text-[#202124] mb-2">{cat.title}</h3>
                <p className="text-[14px] text-[#5f6368] leading-relaxed mb-5">{cat.desc}</p>
                <ul className="space-y-2">
                  {cat.links.map((label, li) => (
                    <li key={li}>
                      <Link to={cat.link} className="group flex items-center gap-2 text-[13px] text-[#ff9a4d] hover:text-[#e8872e] font-medium transition-colors">
                        {label}
                        <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick links */}
      <div className="bg-[#f8f9fa] border-t border-[#e8eaed]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-[18px] font-medium text-[#202124]">Can't find what you're looking for?</h3>
              <p className="text-[14px] text-[#5f6368] mt-1">Our community and support team are here to help.</p>
            </div>
            <div className="flex gap-3">
              <Link to="/community" className="px-5 py-2.5 text-[14px] font-medium text-[#ff9a4d] border border-[#dadce0] hover:bg-white rounded-md transition-colors">Community</Link>
              <Link to="/enterprise" className="px-5 py-2.5 text-[14px] font-medium text-white bg-[#ff9a4d] hover:bg-[#e8872e] rounded-md transition-colors shadow-sm">Contact support</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocsPage;
